import { Container, Graphics, Sprite } from "pixi.js";
import { LayerName } from "../../config/layers";
import { ImageName } from "../../resources/resources";
import { WithOwner } from "./WithOwner";

/**
 * Where a sprite is put when its entity is added: a render layer (the
 * default — flat world stacking, per-layer filters, world or screen
 * coordinates as the layer declares), or "parent" — mounted inside the
 * nearest ancestor entity's mount container (`childMount ?? sprite`),
 * riding that container's transform and drawing in its local
 * coordinates. "parent" placement is only for entities added as
 * children (see Entity.children); adding one with no ancestor sprite
 * to mount in throws.
 *
 * "parent" is a reserved name — the renderer refuses a layer table
 * that declares a layer called "parent".
 */
export type SpritePlacement = LayerName | "parent";

/**
 * An extension of Pixi's Container class that lets us easily specify which layer a
 * sprite should be rendered in, as well as keep track of the entity that owns this sprite.
 */
export interface GameSprite extends Container, WithOwner {
  layerName?: SpritePlacement;
}

export function loadGameSprite(
  name: ImageName,
  layerName?: SpritePlacement,
  options?: { anchor?: [number, number]; size?: [number, number] },
): Sprite & GameSprite {
  const sprite = Sprite.from(name) as Sprite & GameSprite;
  sprite.layerName = layerName;
  if (options?.anchor) {
    sprite.anchor.set(...options.anchor);
  }
  if (options?.size) {
    sprite.setSize(...options.size);
  }
  return sprite;
}

export function createGraphics(
  layerName: SpritePlacement,
): GameSprite & Graphics {
  const graphics = new Graphics() as GameSprite & Graphics;
  graphics.layerName = layerName;
  return graphics;
}
